import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { Auth } from '@angular/fire/auth';
import { CartService, CartItem } from 'src/app/shared/services/cart.service';
import { FirestoreService } from 'src/app/shared/services/firestore.service';
import { UxserviceService } from 'src/app/shared/services/uxservice.service';
import {
  Firestore,
  collection,
  query,
  where,
  onSnapshot,
  Unsubscribe,
} from '@angular/fire/firestore';
import { MatDialog } from '@angular/material/dialog';
import { TermsDialogComponent } from 'src/app/components/terms-dialog/terms-dialog.component';

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss'],
})
export class CartComponent implements OnInit, OnDestroy {
  cartItems$: Observable<CartItem[]>;
  private itemsSub?: Subscription;
  private availabilityUnsub?: Unsubscribe;

  // Map of cart item instance IDs to their availability status
  // true = available, false = unavailable (booked by someone else)
  availabilityMap: { [instanceId: string]: boolean } = {};

  termsAccepted = false;

  constructor(
    private cartService: CartService,
    private router: Router,
    private auth: Auth,
    private firestoreService: FirestoreService,
    private firestore: Firestore,
    private ux: UxserviceService,
    private dialog: MatDialog,
  ) {
    this.cartItems$ = this.cartService.cartItems$;
  }

  ngOnInit(): void {
    // Monitor cart items to setup/refresh availability listeners
    this.itemsSub = this.cartItems$.subscribe((items) => {
      this.setupAvailabilityListener(items);
    });
  }

  ngOnDestroy(): void {
    if (this.itemsSub) this.itemsSub.unsubscribe();
    if (this.availabilityUnsub) this.availabilityUnsub();
  }

  /**
   * Set up a real-time listener for all bookings associated with the equipment in the cart.
   */
  private setupAvailabilityListener(items: CartItem[]) {
    if (this.availabilityUnsub) {
      this.availabilityUnsub();
      this.availabilityUnsub = undefined;
    }

    if (items.length === 0) {
      this.availabilityMap = {};
      return;
    }

    const equipmentIds = [...new Set(items.map((i) => i.equipmentId))];

    // Firestore 'in' query supports up to 30 items
    const bookingsRef = collection(this.firestore, 'booking-list');
    const q = query(
      bookingsRef,
      where('equipmentId', 'in', equipmentIds),
      where('status', 'in', [
        'Pending',
        'Confirmed',
        'Paid',
        'Completed',
        'Accepted',
      ]),
    );

    this.availabilityUnsub = onSnapshot(q, (snapshot) => {
      const allBookings = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...(doc.data() as any),
      }));

      this.checkAllItems(items, allBookings);
    });
  }

  private checkAllItems(items: CartItem[], allBookings: any[]) {
    const newMap: { [id: string]: boolean } = {};

    items.forEach((item) => {
      const itemStart = new Date(item.startDate).getTime();
      const itemEnd = new Date(item.endDate).getTime();

      const hasConflict = allBookings.some((booking) => {
        // We must match the ID of the equipment to ensure we are only checking relevant bookings
        const isSameVehicle = booking.equipmentId === item.equipmentId;
        if (!isSameVehicle) return false;

        const bStart = booking.startDate.toDate
          ? booking.startDate.toDate().getTime()
          : new Date(booking.startDate).getTime();
        const bEnd = booking.endDate.toDate
          ? booking.endDate.toDate().getTime()
          : new Date(booking.endDate).getTime();

        // standard overlap check: (startA <= endB) && (endA >= startB)
        const overlaps = itemStart <= bEnd && itemEnd >= bStart;

        return overlaps;
      });

      newMap[item.id!] = !hasConflict;
    });

    this.availabilityMap = newMap;
  }

  isItemUnavailable(instanceId: string | undefined): boolean {
    if (!instanceId) return false;
    return this.availabilityMap[instanceId] === false;
  }

  hasUnavailableItems(): boolean {
    return Object.values(this.availabilityMap).some(
      (status) => status === false,
    );
  }

  get subTotal(): number {
    return this.cartService.calculateTotal();
  }

  get totalAmount(): number {
    return this.subTotal;
  }

  async removeItem(instanceId: string | undefined) {
    const confirmed = await this.ux.showConfirmation(
      'Are you sure you want to remove this item from your cart?',
      'Remove Item',
      'Yes',
      'No',
    );

    if (confirmed) {
      this.cartService.removeFromCart(instanceId);
    }
  }

  editItem(item: CartItem) {
    this.router.navigate(['/booking-detail', item.equipmentId], {
      queryParams: { editCartItemId: item.id },
    });
  }

  async proceedToConfirm() {
    if (this.hasUnavailableItems()) {
      this.ux.showError('Please remove unavailable items before proceeding.');
      return;
    }

    const user = this.auth.currentUser;

    // 1. If not logged in -> Redirect to Login
    if (!user) {
      this.router.navigate(['/login'], {
        queryParams: { returnUrl: '/cart' },
      });
      return;
    }

    // 2. If logged in -> Check Business Details
    try {
      const profile: any = await this.firestoreService.getAuthUserProfile();

      // In this app, a user is considered "pending business details" if they are marked as isIndividual: true
      // or if core business details are missing.
      if (!profile || profile === 'No Auth User' || profile.isIndividual) {
        this.router.navigate(['/setup-role'], {
          queryParams: {
            type: 'customer',
            returnUrl: '/cart',
          },
        });
        return;
      }

      // 3. All good -> Proceed to Confirm Enquiry
      this.router.navigate(['/confirm-enquiry', 'cart']);
    } catch (error) {
      console.error('Error checking user profile:', error);
      // Default to safe side: redirect to setup-role if check fails but logged in
      this.router.navigate(['/setup-role'], {
        queryParams: { type: 'customer', returnUrl: '/confirm-enquiry/cart' },
      });
    }
  }

  onTermsCheckboxChange(event: any) {
    if (event.target.checked) {
      // Force uncheck visually while dialog is open or until accepted
      // We'll set it to the actual model value which is currently false
      event.target.checked = false; 
      this.openTermsDialog();
    } else {
      this.termsAccepted = false;
    }
  }

  openTermsDialog(event?: Event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const dialogRef = this.dialog.open(TermsDialogComponent, {
      width: '1200px',
      panelClass: 'custom-dialog-container',
      backdropClass: 'blurred-backdrop',
      disableClose: true // Force explicit accept/decline action if needed, or just handle null
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.termsAccepted = true;
      } else {
        this.termsAccepted = false;
      }
    });
  }
}
